import { z } from 'zod'

// Mirrors CreateCaseDto (apps/nivex-backend/src/features/cases/dto/create-case.dto.ts) exactly —
// `pan` is optional there (the schema this replaced wrongly required it), and the field is
// `mobile`, not `phone`. `build_portfolio` creates a bare case with no goals/profile yet — staff
// fills those in afterward via the case-detail Goals tab (`case-goals.tsx`).
export const createCaseSchema = z.object({
  serviceType: z.enum(['portfolio_audit', 'trades_audit', 'build_portfolio']),
  name: z.string().min(2, 'Enter the client’s full name.'),
  email: z.string().email('Enter a valid email address.'),
  mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.'),
  // `@IsOptional()` on the backend DTO only skips validation for undefined/null, not an empty
  // string — preprocessing '' to undefined here (rather than a bare `.optional()`) is what lets a
  // blank PAN field actually skip the regex instead of failing it.
  pan: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z
      .string()
      .regex(/^[A-Za-z]{5}\d{4}[A-Za-z]$/, 'Enter a valid PAN, e.g. ABCPS1234D.')
      .transform((v) => v.toUpperCase())
      .optional(),
  ),
  city: z.string().optional(),
  seriousnessStatus: z.enum(['pending', 'qualified', 'not_needed', 'failed']).optional(),
})
export type CreateCaseFormValues = z.infer<typeof createCaseSchema>

// Mirrors UpdateCaseDto — all three optional at the schema level, matching the DTO. The backend's
// own "at least one field required" rule is satisfied by only ever sending fields the form holds,
// not re-validated here as a cross-field .refine().
export const caseDetailsSchema = z.object({
  note: z.string().max(2000, 'Keep the note under 2000 characters.').optional(),
  nextFollowUpAt: z.string().optional(),
  // `z.coerce.number()` alone would turn a cleared input ('') into 0 (`Number('') === 0`), which
  // would silently zero the score — preprocess blank/undefined input to undefined first so the
  // field stays genuinely unset instead of coercing to a real value.
  wealthScore: z.preprocess(
    (v) => (v === '' || v === undefined || v === null ? undefined : v),
    z.coerce.number().int().min(0).max(100).optional(),
  ),
})
export type CaseDetailsFormValues = z.infer<typeof caseDetailsSchema>

// Mirrors SaveInvestorProfileDto exactly — `pan` is required here (unlike createCaseSchema's
// optional pan), since this endpoint is what actually creates the investor's login-capable
// account.
export const saveInvestorProfileSchema = z.object({
  name: z.string().min(2, 'Enter the client’s full name.'),
  email: z.string().email('Enter a valid email address.'),
  mobile: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.'),
  pan: z
    .string()
    .regex(/^[A-Za-z]{5}\d{4}[A-Za-z]$/, 'Enter a valid PAN, e.g. ABCPS1234D.')
    .transform((v) => v.toUpperCase()),
  city: z.string().optional(),
  // Blank-skips-validation, same convention as `investor-forms.schema.ts`'s `updateInvestorSchema`
  // — enrichment-only server-side (never overwrites an already-set age).
  age: z.preprocess(
    (v) => (v === '' || v === undefined ? undefined : v),
    z.coerce.number().int().min(1, 'Enter a valid age.').max(120, 'Enter a valid age.').optional(),
  ),
})
export type SaveInvestorProfileFormValues = z.infer<typeof saveInvestorProfileSchema>

// Mirrors CreateHoldingDto exactly — money/quantity fields stay a plain numeric UI (matching the
// existing type="number" inputs) but format to the backend's fixed-precision Decimal strings via
// `.transform()`, so a Decimal-shaped value is never parsed back to a lossy `number` after this
// boundary.
export const createCaseHoldingSchema = z.object({
  scheme: z.string().min(2, 'Enter the scheme name.'),
  category: z.enum(['equity', 'debt', 'hybrid', 'gold']),
  units: z.coerce
    .number()
    .nonnegative('Units can’t be negative.')
    .transform((v) => v.toFixed(4)),
  investedValue: z.coerce
    .number()
    .nonnegative('Invested value can’t be negative.')
    .transform((v) => v.toFixed(2)),
  currentValue: z.coerce
    .number()
    .nonnegative('Current value can’t be negative.')
    .transform((v) => v.toFixed(2)),
  // May be negative — matches the backend's regex, unlike units/invested/current above.
  xirr: z.coerce.number().transform((v) => v.toFixed(2)),
})
export type CreateCaseHoldingFormValues = z.infer<typeof createCaseHoldingSchema>

// Mirrors CreateTradeDto.
export const createCaseTradeSchema = z.object({
  symbol: z.string().min(1, 'Enter the trading symbol.'),
  segment: z.enum(['equity', 'fno', 'commodity']),
  side: z.enum(['buy', 'sell']),
  qty: z.coerce
    .number()
    .positive('Quantity must be greater than 0.')
    .transform((v) => v.toFixed(4)),
  price: z.coerce
    .number()
    .positive('Price must be greater than 0.')
    .transform((v) => v.toFixed(2)),
  pnl: z.coerce.number().transform((v) => v.toFixed(2)),
  tradedAt: z.string().min(1, 'Pick a trade date.'),
})
export type CreateCaseTradeFormValues = z.infer<typeof createCaseTradeSchema>

// Mirrors CreatePrescriptionStepDto — real, structured fields (no client-supplied `position`; the
// backend always appends). Used only by the real (staff-editable) add-step form in
// case-prescription.tsx — the investor-dashboard path never renders it (`canEdit` is never true
// there).
export const createCasePrescriptionStepSchema = z.object({
  title: z.string().min(2, 'Give the step a short title.'),
  description: z.string().min(3, 'Describe what the client should do.'),
  priority: z.enum(['high', 'medium', 'low']),
  impactPoints: z.coerce.number().int().min(0).max(100),
})
export type CreateCasePrescriptionStepFormValues = z.infer<typeof createCasePrescriptionStepSchema>

export const editCasePrescriptionStepSchema = createCasePrescriptionStepSchema
export type EditCasePrescriptionStepFormValues = CreateCasePrescriptionStepFormValues

// Mirrors CreateCallLogDto. `durationMin` (not `durationSec`) stays the UI-facing field name to
// match today's form — converted to seconds at the API-call boundary. `followUpAt` is only
// rendered/sent when `outcome === 'follow_up_scheduled'`; the schema itself just allows it to be
// optional.
// Mirrors CreateCallLogDto's note requirement: required for every outcome except no_answer —
// there's nothing to say about a call that never connected.
export const logCaseCallSchema = z
  .object({
    outcome: z.enum(['connected', 'no_answer', 'follow_up_scheduled', 'completed']),
    durationMin: z.coerce.number().min(0, 'Duration can’t be negative.').optional(),
    notes: z.string().max(500, 'Keep it under 500 characters.').optional(),
    followUpAt: z.string().optional(),
  })
  .refine((v) => v.outcome === 'no_answer' || !!v.notes?.trim(), {
    message: 'Add a note for this outcome.',
    path: ['notes'],
  })
export type LogCaseCallFormValues = z.infer<typeof logCaseCallSchema>

// Mirrors CreateCaseDocumentDto's non-file fields — the file itself stays plain `useState<File |
// null>` component state (matching this codebase's existing precedent; RHF has no clean two-way
// binding for `<input type="file">`, and the real validation that matters — magic-byte sniffing —
// happens server-side regardless of anything client-side can check). `password` is cross-field
// validated against `kind` here for UX only; the backend's own 422 is still the real enforcement.
// Same PAN shape used across the app's investor-profile forms (`investor-forms.schema.ts`) — a CAS
// password is most often the investor's own PAN.
const PAN_FORMAT = /^[A-Za-z]{5}\d{4}[A-Za-z]$/

export const uploadCaseDocumentSchema = z
  .object({
    kind: z.enum(['cas_pdf', 'broker_xlsx', 'advisor_report']),
    password: z.string().optional(),
  })
  .refine((v) => v.kind === 'cas_pdf' || !v.password, {
    message: 'A password only applies to a CAS statement.',
    path: ['password'],
  })
  // A CAS password is usually the investor's PAN, but some CAS files use a KFintech password
  // instead — so this accepts either shape and only rejects empty/trivial garbage, not a
  // non-PAN string outright.
  .refine(
    (v) =>
      v.kind !== 'cas_pdf' ||
      !v.password ||
      PAN_FORMAT.test(v.password) ||
      v.password.trim().length >= 4,
    {
      message: 'Enter your CAS password — your PAN, or your KFintech password.',
      path: ['password'],
    },
  )
export type UploadCaseDocumentFormValues = z.infer<typeof uploadCaseDocumentSchema>

/** Client-side-only pre-check, duplicated deliberately from the backend's own `MAX_DOCUMENT_BYTES`
 * so an oversized file is rejected immediately instead of round-tripping to a raw framework 413. */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024

export const addHoldingSchema = z.object({
  scheme: z.string().min(2, 'Enter the scheme name.'),
  category: z.enum(['Equity', 'Debt', 'Hybrid', 'Gold']),
  units: z.coerce.number().positive('Units must be greater than 0.'),
  investedValue: z.coerce.number().positive('Invested value must be greater than 0.'),
  currentValue: z.coerce.number().positive('Current value must be greater than 0.'),
  xirr: z.coerce.number(),
})
export type AddHoldingFormValues = z.infer<typeof addHoldingSchema>

// Same field set/validation as addHoldingSchema — an edit dialog is the same form seeded with the
// holding's current values, not a different shape. Reused directly for the bulk-edit table's rows.
export const editHoldingSchema = addHoldingSchema
export type EditHoldingFormValues = AddHoldingFormValues

export const bulkHoldingsSchema = z.array(addHoldingSchema)
export type BulkHoldingsFormValues = z.infer<typeof bulkHoldingsSchema>

export const addStaffSchema = z.object({
  name: z.string().min(2, 'Enter the staff member’s full name.'),
  email: z.string().email('Enter a valid work email address.'),
  // Stricter than this file's other phone fields (`^[6-9]`, not just digit-count) — matches
  // the backend's `CreateStaffDto` exactly, so an invalid number is caught inline instead of
  // surfacing as a 400 after submit. Also used as this staff member's Plivo caller-id.
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.'),
})
export type AddStaffFormValues = z.infer<typeof addStaffSchema>

export const editStaffSchema = z.object({
  name: z.string().min(2, 'Enter the staff member’s full name.'),
  email: z.string().email('Enter a valid work email address.'),
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number.'),
})
export type EditStaffFormValues = z.infer<typeof editStaffSchema>

export const addTradeSchema = z.object({
  symbol: z.string().min(1, 'Enter the trading symbol.'),
  segment: z.enum(['Equity', 'F&O', 'Commodity']),
  side: z.enum(['BUY', 'SELL']),
  qty: z.coerce.number().positive('Quantity must be greater than 0.'),
  price: z.coerce.number().positive('Price must be greater than 0.'),
  tradedAt: z.string().min(1, 'Pick a trade date.'),
  pnl: z.coerce.number(),
})
export type AddTradeFormValues = z.infer<typeof addTradeSchema>

// Same field set/validation as addTradeSchema — an edit dialog is the same form seeded with the
// trade's current values, not a different shape. Reused directly for the bulk-edit table's rows.
export const editTradeSchema = addTradeSchema
export type EditTradeFormValues = AddTradeFormValues

export const bulkTradesSchema = z.array(addTradeSchema)
export type BulkTradesFormValues = z.infer<typeof bulkTradesSchema>

const goalTargetAgeField = z.coerce
  .number()
  .int()
  .min(1, 'Enter a valid age.')
  .max(120, 'Enter a valid age.')
const goalYearsField = z.coerce
  .number()
  .int()
  .min(1, 'Enter a valid number of years.')
  .max(80, 'Enter a valid number of years.')
// Deliberately stricter than `CreateGoalDto`'s `DECIMAL_2DP` regex (which allows "0.00"): a goal's
// present-day cost or monthly expense can't be ₹0 and still mean anything.
const goalMoneyField = (message: string) =>
  z.coerce
    .number()
    .positive(message)
    .transform((v) => v.toFixed(2))

// Mirrors `CreateGoalDto`'s per-`GoalKey` conditional shape exactly (same discriminated-union
// pattern as `features/build-portfolio/schemas/buildPortfolioForms.schema.ts`'s `goalSchema` —
// duplicated rather than cross-imported, since that feature's schema is intake-only and has no
// concept of the staff-only plan fields `editGoalSchema` below adds).
export const createGoalSchema = z.discriminatedUnion('key', [
  z.object({ key: z.literal('wealth'), years: goalYearsField }),
  z.object({
    key: z.literal('retirement'),
    targetAge: goalTargetAgeField,
    monthlyExpense: goalMoneyField('Enter the monthly expense.'),
  }),
  z.object({
    key: z.literal('home'),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
  }),
  z.object({
    key: z.literal('education'),
    childName: z.string().optional(),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
  }),
  z.object({
    key: z.literal('marriage'),
    childName: z.string().optional(),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
  }),
])
export type CreateGoalFormValues = z.infer<typeof createGoalSchema>

// Blank-string-skips-validation preprocessing, same convention as `createCaseSchema`'s `pan` — an
// untouched optional field submits as `undefined`, not a failed regex/coerce on an empty string.
const optionalGoalMoneyField = z.preprocess(
  (v) => (v === '' || v === undefined ? undefined : v),
  z.coerce
    .number()
    .nonnegative('This can’t be negative.')
    .transform((v) => v.toFixed(2))
    .optional(),
)
// Blank skips validation, same convention as `optionalGoalMoneyField` — entered as a percentage
// (e.g. "6"), converted to the 0–1 rate the backend stores/expects (e.g. 0.06) before submit, same
// unit-conversion pattern already used for money fields elsewhere in this schema file.
const optionalInflationRatePercentField = z.preprocess(
  (v) => (v === '' || v === undefined ? undefined : v),
  z.coerce
    .number()
    .min(0, 'This can’t be negative.')
    .max(100, 'Enter a percentage between 0 and 100.')
    .transform((v) => v / 100)
    .optional(),
)
// The "Edit details" fields — same per-`GoalKey` required shape as `createGoalSchema` (a goal's
// own type never changes after creation) plus the two staff-only tracking fields every goal type
// shares (`invested`, `priority`). Deliberately kept separate from `goalPlanFormSchema` below —
// the pixel-perfect goal dialog (`case-goals.tsx`'s `GoalDetailDialog`) submits these two forms
// independently, so editing a goal's target age never touches its fund/SIP/note (which the backend
// treats as a distinct "reset to pending review" trigger — see `goalPlanFormSchema`'s own comment).
export const editGoalDetailsSchema = z.discriminatedUnion('key', [
  z.object({
    key: z.literal('wealth'),
    years: goalYearsField,
    invested: optionalGoalMoneyField,
    inflationRate: optionalInflationRatePercentField,
    priority: z.enum(['high', 'medium', 'low']).optional(),
  }),
  z.object({
    key: z.literal('retirement'),
    targetAge: goalTargetAgeField,
    monthlyExpense: goalMoneyField('Enter the monthly expense.'),
    invested: optionalGoalMoneyField,
    inflationRate: optionalInflationRatePercentField,
    priority: z.enum(['high', 'medium', 'low']).optional(),
  }),
  z.object({
    key: z.literal('home'),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
    invested: optionalGoalMoneyField,
    inflationRate: optionalInflationRatePercentField,
    priority: z.enum(['high', 'medium', 'low']).optional(),
  }),
  z.object({
    key: z.literal('education'),
    childName: z.string().optional(),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
    invested: optionalGoalMoneyField,
    inflationRate: optionalInflationRatePercentField,
    priority: z.enum(['high', 'medium', 'low']).optional(),
  }),
  z.object({
    key: z.literal('marriage'),
    childName: z.string().optional(),
    targetAge: goalTargetAgeField,
    presentCost: goalMoneyField('Enter the present-day cost.'),
    invested: optionalGoalMoneyField,
    inflationRate: optionalInflationRatePercentField,
    priority: z.enum(['high', 'medium', 'low']).optional(),
  }),
])
export type EditGoalDetailsFormValues = z.infer<typeof editGoalDetailsSchema>

// The "Plan for this goal" section's own form — same shape regardless of `GoalKey`, mirroring the
// reference design's `GoalPlanSection` exactly (fund + SIP both required to save a plan at all;
// note optional). Submitting this always resets `planStatus` back to `pending` server-side
// (`cases.service.ts`'s `updateGoal`) — approval is its own dedicated action
// (`useSetCaseGoalPlanApproved`), never a field on this form.
// One fund row within a goal's plan — a goal can have several (see `GoalFund`'s own schema
// comment in `schema.prisma`), each added/edited/removed through its own dedicated endpoint, so
// this schema covers exactly one fund's own two fields, not the whole plan.
export const goalFundFormSchema = z.object({
  fundName: z.string().min(1, 'Enter the recommended fund.'),
  monthlySip: goalMoneyField('Enter the monthly SIP.'),
})
export type GoalFundFormValues = z.infer<typeof goalFundFormSchema>

// The plan's own note — decoupled from its fund list (funds are mutated one at a time through
// their own endpoints, never bundled into this form).
export const goalPlanNoteFormSchema = z.object({
  planNote: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string().max(1000, 'Keep it under 1000 characters.').optional(),
  ),
})
export type GoalPlanNoteFormValues = z.infer<typeof goalPlanNoteFormSchema>

// Mirrors `UpsertBuildProfileDto` — same shape as the public onboarding wizard's own
// `buildExpensesSchema` (`features/build-portfolio/schemas/buildPortfolioForms.schema.ts`),
// duplicated rather than cross-imported for the same reason as `createGoalSchema` above.
export const upsertBuildProfileSchema = z.object({
  monthlyIncome: z.coerce
    .number()
    .positive('Enter the monthly income.')
    .transform((v) => v.toFixed(2)),
  rentEmi: z.coerce
    .number()
    .nonnegative('This can’t be negative.')
    .transform((v) => v.toFixed(2)),
  utilitiesGroceries: z.coerce
    .number()
    .nonnegative('This can’t be negative.')
    .transform((v) => v.toFixed(2)),
  transportLifestyle: z.coerce
    .number()
    .nonnegative('This can’t be negative.')
    .transform((v) => v.toFixed(2)),
  otherExpenses: z.coerce
    .number()
    .nonnegative('This can’t be negative.')
    .transform((v) => v.toFixed(2)),
})
export type UpsertBuildProfileFormValues = z.infer<typeof upsertBuildProfileSchema>
